import {
  ButtonOptions,
  CustomerInfo,
  ExternalUserDetail,
  FastOrder,
  Lead,
  MessageType,
  Offer,
  TrackAddToCart,
  TrackDeeplinkJson,
  TrackFormBase,
  TrackFormFieldError,
  TrackFormFieldInteraction,
  TrackMessage,
  TrackModalAction,
  TrackModalOpen,
  TrackOrderSubmitSuccess,
  TrackPageLoaded,
  TrackTabChange,
  UpsellOrderSubmitSuccess,
  UpsellSummary,
  WireframeElementId,
  WireframeId,
} from './adobeDataLayer.types';
import { FocusEvent, MouseEvent, MouseEventHandler } from 'react';

export const isMessageType = (value: string): value is MessageType =>
  Object.values(MessageType).includes(value as MessageType);

const isWfId = (s: string): s is WireframeId =>
  /^(\d{1,2})(\.\d{1,2})*\.{0,1}[a-zA-Z]{0,1}((-)[a-zA-Z-]{1,})*$/.test(s);
const isWfElementId = (s: string): s is WireframeElementId =>
  /^(\d{1,2})(\.\d{1,2})*\.{0,1}[a-zA-Z]{0,1}_(\d{3})$/.test(s);

let fieldInteractions: string[] | undefined;

const getFieldInteractionsStorage = () => {
  if (!fieldInteractions) {
    fieldInteractions = [];
  }

  return fieldInteractions;
};

let fieldErrors: string[] | undefined;

const getFieldErrorsStorage = () => {
  if (!fieldErrors) {
    fieldErrors = [];
  }

  return fieldErrors;
};

const onRedirectTimeout = (fn: () => void) => setTimeout(fn, 500);

const getTwoDigits = (num: number) => (num.toString().length === 2 ? num.toString() : `0${num}`);

export const getAdobeDataLayer = () => {
  const formLeave = (formOrWfId: string, formSectionName?: string) => {
    !(isWfElementId(formOrWfId) || isWfId(formOrWfId)) &&
      console.warn(`[form:leave] malformed form Id "${formOrWfId}" given.`);
    window.adobeDataLayer.push({
      event: 'form:leave',
      eventData: {
        formElmId: formOrWfId,
        formSection: formSectionName,
      },
    });
  };

  const findClosestElementWithAttribute = (el: Element, attribute: string): undefined | string =>
    el.closest(`*[${attribute}]`)?.getAttribute(attribute) ?? undefined;

  const buttonClick = (e: MouseEvent, options?: ButtonOptions) => {
    window.adobeDataLayer.push({
      event: 'buttonClick',
      type: findClosestElementWithAttribute(e.currentTarget, 'type'),
      element: findClosestElementWithAttribute(e.currentTarget, 'data-elm'),
      ...options,
    });
  };

  const trackButtonClick = (
    callbackOrEvent: MouseEvent | MouseEventHandler,
    options?: ButtonOptions
  ): MouseEventHandler | undefined => {
    if (typeof callbackOrEvent === 'object') {
      buttonClick(callbackOrEvent, options);
    } else {
      return (e) => {
        buttonClick(e, options);
        callbackOrEvent?.(e);
      };
    }
  };

  return {
    trackPageLoaded: (params: TrackPageLoaded) => {
      window.adobeDataLayer.push({ ...params, themeVersion: `O2 UI ${process.env.NPM_PACKAGE_VERSION}` });
      window.adobeDataLayer.push({ event: 'pageLoaded' });
    },
    trackButtonClick,
    trackKahl: (message: TrackMessage) => window.adobeDataLayer.push({ event: 'popupMessage', eventData: message }),
    trackFormOpen: (formOrWfId: string, formSectionName?: string) => {
      if (!(isWfElementId(formOrWfId) || isWfId(formOrWfId)))
        console.warn(`[trackFormOpen] malformed form Id "${formOrWfId}" given.`);

      window.adobeDataLayer.push({
        event: 'form:open',
        eventData: {
          formElmId: formOrWfId,
          formSection: formSectionName,
        },
      });
    },
    trackFormSubmit:
      ({ formOrWfId, formSectionName }: TrackFormBase, callback?: (e?: MouseEvent) => void) =>
      (e?: MouseEvent) => {
        if (!(isWfElementId(formOrWfId) || isWfId(formOrWfId)))
          console.warn(`[trackFormSubmit] malformed form Id "${formOrWfId}" given.`);

        window.adobeDataLayer.push({
          event: 'form:submit',
          eventData: {
            formElmId: formOrWfId,
            formSectionName,
          },
        });
        callback?.(e);
      },
    trackFormLeave: ({ formOrWfId, formSectionName }: TrackFormBase, callback?: (e?: MouseEvent) => void) =>
      callback
        ? (e?: MouseEvent) => {
            formLeave(formOrWfId, formSectionName);
            callback(e);
          }
        : formLeave(formOrWfId, formSectionName),
    trackFormFieldFirstInteraction:
      (
        { formOrWfId, formSectionName, fieldLabel, fieldElmId }: TrackFormFieldInteraction,
        callback?: (e?: FocusEvent) => void
      ) =>
      (e?: FocusEvent) => {
        if (!getFieldInteractionsStorage().includes(`${formOrWfId}-${fieldElmId}`)) {
          if (!isWfId(formOrWfId))
            console.warn(`[trackFormFieldFirstInteraction] malformed form Id "${formOrWfId}" given.`);

          if (!isWfElementId(fieldElmId))
            console.warn(`[trackFormFieldFirstInteraction] malformed field Id "${fieldElmId}" given.`);

          getFieldInteractionsStorage().push(`${formOrWfId}-${fieldElmId}`);
          window.adobeDataLayer.push({
            event: 'form:field:firstInteraction',
            eventData: {
              formElmId: formOrWfId,
              formSection: formSectionName,
              fieldLabel: fieldLabel,
              fieldElmId: fieldElmId,
            },
          });
        }

        callback?.(e);
      },
    trackFormFieldError: ({ fieldElmId, error, type = 'validation' }: TrackFormFieldError) => {
      if (!getFieldErrorsStorage().includes(fieldElmId)) {
        if (!isWfElementId(fieldElmId)) console.warn(`[trackFormFieldError] malformed field Id "${fieldElmId}" given.`);

        getFieldErrorsStorage().push(fieldElmId);
        window.adobeDataLayer.push({
          event: 'form:error',
          eventData: {
            type,
            description: error,
            fieldElmId: fieldElmId,
          },
        });
      }
    },
    trackModalOpen: ({ wireframeId, heading, variant }: TrackModalOpen) => {
      if (!isWfId(wireframeId)) console.warn(`[trackModalOpen] malformed modal Id "${wireframeId}" given.`);

      window.adobeDataLayer.push({
        event: 'modal:open',
        eventData: {
          modalId: wireframeId,
          modalHeading: heading,
          modalVariant: variant,
        },
      });
    },
    trackModalAction: ({ action, actionType, wireframeId, heading, variant }: TrackModalAction) => {
      if (!isWfId(wireframeId)) console.warn(`[trackModalAction] malformed modal Id "${wireframeId}" given.`);

      window.adobeDataLayer.push({
        event: 'modal:action',
        eventData: {
          modalAction: action,
          modalActionType: actionType,
          modalId: wireframeId,
          modalHeading: heading,
          modalVariant: variant,
        },
      });
    },
    trackAddToCart:
      (
        {
          id,
          offerSourceType,
          unicaAudienceId,
          unicaTreatmentCode,
          unicaAudienceIdType,
          onlineOfferCode,
        }: TrackAddToCart,
        callback?: (e?: MouseEvent) => void
      ) =>
      (e?: MouseEvent) => {
        window.adobeDataLayer.push({
          event: 'addToCart',
          eventData: {
            id,
            unicaAudienceId,
            unicaTreatmentCode,
            unicaAudienceIdType,
            onlineOfferCode,
            offerSourceType,
          },
        });
        callback?.(e);
      },
    trackOrderSubmitSuccess: ({ order, products }: TrackOrderSubmitSuccess) =>
      window.adobeDataLayer.push({
        event: 'orderSubmitSuccess',
        eventData: {
          order,
          products,
        },
      }),
    trackOffersShow: (offers: Offer[]) =>
      window.adobeDataLayer.push({
        event: 'offersShow',
        eventData: {
          offers,
        },
      }),
    trackOfferClick: (offer: Offer, callback?: (e?: MouseEvent) => void) => (e?: MouseEvent) => {
      window.adobeDataLayer.push({
        event: 'offerClick',
        eventData: {
          ...offer,
        },
      });

      callback?.(e);

      if (e && e.currentTarget instanceof HTMLAnchorElement) {
        e.preventDefault();

        const target = e.currentTarget as HTMLAnchorElement;

        target?.href && onRedirectTimeout(() => target?.href && window.open(target.href, target.target || '_self'));
      }
    },
    trackOfferDetailShow: (offer: Offer, callback: (e?: MouseEvent) => void) => (e?: MouseEvent) => {
      window.adobeDataLayer.push({
        event: 'offerDetailShow',
        eventData: {
          ...offer,
        },
      });
      callback?.(e);
    },
    trackOfferRejected: (offer: Offer, callback: (e?: MouseEvent) => void) => (e?: MouseEvent) => {
      window.adobeDataLayer.push({
        event: 'offerRejected',
        eventData: {
          ...offer,
        },
      });
      callback?.(e);
    },
    trackUser: (customer: CustomerInfo, externalUser?: ExternalUserDetail) => {
      window.adobeDataLayer.push(
        JSON.parse(
          JSON.stringify({
            user: {
              userType: 'external',
              basicServiceType: 'postpaid',
              customerRefNo: customer.cuRefNo,
              customerRefSegment: customer.segment,
              userNameE110: customer.primaryAdmin?.email,
              msisdnE110: customer.primaryAdmin?.phoneNoMobile,
              externalUserRole:
                externalUser &&
                externalUser.entityAccesses
                  ?.filter((ea) => ea.cuRefNo === customer.cuRefNo)
                  .filter((ea) => ea.isTrusted)
                  .map((ea) => ea.roleType),
              customerName: customer.displayName,
              customerCareLevel: customer.customerCareLevel,
              customerSubjectType: customer.subjectType,
              customerMarketingAgreement: customer.marketingAgreement,
              ...(externalUser
                ? {
                    userName: externalUser.login,
                    msisdn: externalUser.phoneNoMobile,
                    userFirstName: externalUser.firstName,
                    userLastName: externalUser.lastName,
                  }
                : undefined),
            },
          })
        )
      );
    },
    trackUserInternal: (username: string) =>
      window.adobeDataLayer.push({
        userInternal: {
          userType: 'internal',
          userName: username,
        },
      }),
    trackTabChange: ({ wfElementId, tabName }: TrackTabChange) => {
      if (!isWfElementId(wfElementId))
        console.warn(`[trackTabChange] malformed wireframe and element ID "${wfElementId}" given.`);

      window.adobeDataLayer.push({
        event: 'tabChange',
        eventData: {
          tabName: tabName,
          elementId: wfElementId,
        },
      });
    },
    trackLeadInsert: ({ wireframeId, ...lead }: Lead) => {
      if (!isWfId(wireframeId)) console.warn(`[trackModalAction] malformed modal Id "${wireframeId}" given.`);

      window.adobeDataLayer.push({
        event: 'leadInsert',
        eventData: {
          ...lead,
          formId: wireframeId,
        },
      });
    },
    trackFastOrder: ({ orderRefNo, sentAt, originalTariffCode, newTariffCode, ...fastOrder }: FastOrder) => {
      window.adobeDataLayer.push({
        event: 'fastOrder',
        eventData: {
          ...fastOrder,
          fastOrderId: orderRefNo,
          sendtime: `${sentAt.getFullYear() - 2000}${getTwoDigits(sentAt.getMonth())}${getTwoDigits(
            sentAt.getDay()
          )}-${getTwoDigits(sentAt.getHours())}${getTwoDigits(sentAt.getMinutes())}`,
          originalId: originalTariffCode,
          id: newTariffCode,
        },
      });
    },
    trackUpsellSummary: ({ upsellComponent, productGroup, productCode, assetRefNo, products }: UpsellSummary) => {
      window.adobeDataLayer.push({
        event: 'tariffChangeUpsell:summary',
        eventData: {
          type: upsellComponent,
          productGroup,
          productDetail: {
            productCode,
            assetRefNo,
          },
        },
        products,
      });
    },
    trackUpsellOrderSubmitSuccess: ({
      upsellComponent,
      productGroup,
      productCode,
      assetRefNo,
      products,
      order,
    }: UpsellOrderSubmitSuccess) => {
      window.adobeDataLayer.push({
        event: 'tariffChangeUpsell:orderSubmitSuccess',
        eventData: {
          type: upsellComponent, // typ prvku, který kde uživatel potvrdil objednávku
          productGroup,
          productDetail: {
            productCode,
            assetRefNo,
          },
        },
        products,
        order,
      });
    },
    trackDeeplinkJson: (params: TrackDeeplinkJson) => {
      window.adobeDataLayer.push({ deeplinkJson: { ...params } });
    },
  };
};

let adobeScript: HTMLScriptElement | null = null;

export const initAdobeScript = (adobeLaunchPath: string) => {
  if (!adobeScript && adobeLaunchPath) {
    adobeScript = document.createElement('script');
    adobeScript.src = adobeLaunchPath;
    adobeScript.async = true;

    document.head.appendChild(adobeScript);
  }
};
